import { MenuOutlined } from "@ant-design/icons";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DemoBookingModal from "../components/DemoBookingModal";

const features = [
  {
    title: "Streamlined Loan Processing",
    body:
      "Process loans in minutes instead of days with automated workflows and approval processes.",
    icon: "bolt",
  },
  {
    title: "Comprehensive Analytics",
    body:
      "Make data-driven decisions with powerful reporting tools and real-time dashboards.",
    icon: "analytics",
  },
  {
    title: "Mobile Accessibility",
    body: "Manage your loan portfolio from anywhere with our responsive mobile interface.",
    icon: "smartphone",
  },
  {
    title: "Enhanced Security",
    body: "Bank-grade encryption and security protocols protect sensitive financial data.",
    icon: "shield_lock",
  },
];

const steps = [
  {
    title: "Apply in Minutes",
    body: "Complete our intuitive online form with basic details in just a few clicks.",
    icon: "assignment_add",
  },
  {
    title: "Get Instant Decision",
    body: "Receive a personalized quote and approval status immediately through our portal.",
    icon: "verified_user",
  },
  {
    title: "Receive Funds",
    body: "Funds are transferred directly to your bank account within 24 business hours.",
    icon: "account_balance",
  },
];

/** Top bar — scroll targets (matches section ids on page) */
const primaryNavLinks = [
  { label: "Features", id: "features" },
  { label: "Download app", id: "mobile" },
  { label: "Contact", id: "contact" },
  { label: "Join Kwacha Plus", id: "cta" },
];

function isUserLoggedIn() {
  if (typeof window === "undefined") return false;
  return (
    localStorage.getItem("isAuth") === "true" &&
    !!localStorage.getItem("token")
  );
}

/** Staff dashboard vs borrower portal */
function getAccountPath() {
  try {
    const raw = localStorage.getItem("user");
    if (raw) {
      const user = JSON.parse(raw);
      const roleName =
        typeof user.role === "object" ? user.role?.name : user.role;
      const storedRole = localStorage.getItem("userRole");
      if (
        roleName === "Borrower" ||
        storedRole === "Borrower"
      ) {
        return "/borrowerProfile";
      }
    }
  } catch {
    /* ignore */
  }
  return "/dashboard";
}

const testimonials = [
  {
    quote:
      "Kwacha Plus has transformed how we manage our loan portfolio. Processing time has been reduced by 60% and we've seen a significant improvement in collection rates.",
    author: "Michael Tembo",
    org: "First Capital Bank",
  },
  {
    quote:
      "The mobile accessibility and automated reminders have helped us expand into rural areas while maintaining excellent repayment rates. Worth every kwacha!",
    author: "Sarah Mulenga",
    org: "Microfinance Zambia",
  },
  {
    quote:
      "The analytics and reporting capabilities give us insights we never had before. Decision-making is now data-driven and our operational efficiency is up by 40%.",
    author: "David Banda",
    org: "SME Finance Solutions",
  },
];

/** Match sticky header height + breathing room (must match scroll spy offset) */
const NAV_SCROLL_PADDING = 88;

function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - NAV_SCROLL_PADDING;
  window.scrollTo({ top, behavior: "smooth" });
}

function StarRow() {
  return (
    <div className="mb-4 flex gap-0.5 text-accent" aria-hidden>
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className="text-lg leading-none">
          ★
        </span>
      ))}
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const [navOpen, setNavOpen] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const [testimonialIdx, setTestimonialIdx] = useState(0);
  const [activeSectionId, setActiveSectionId] = useState(null);
  const parallaxSectionRef = useRef(null);
  const parallaxLayerRef = useRef(null);

  const loggedIn = isUserLoggedIn();
  const accountPath = getAccountPath();

  /** Scroll spy — highlight nav link whose section is current (top accent = active) */
  useEffect(() => {
    const updateActive = () => {
      const y = window.scrollY + NAV_SCROLL_PADDING;
      let active = null;
      for (const { id } of primaryNavLinks) {
        const el = document.getElementById(id);
        if (el && el.offsetTop <= y) active = id;
      }
      setActiveSectionId(active);
    };
    updateActive();
    window.addEventListener("scroll", updateActive, { passive: true });
    window.addEventListener("resize", updateActive, { passive: true });
    return () => {
      window.removeEventListener("scroll", updateActive);
      window.removeEventListener("resize", updateActive);
    };
  }, []);

  useEffect(() => {
    const isAuth = localStorage.getItem("isAuth");
    if (isAuth !== "true") {
      localStorage.clear();
      localStorage.setItem("isAuth", "false");
    }
  }, []);

  useEffect(() => {
    document.body.style.overflow = navOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [navOpen]);

  /** Keep marketing page pure white (AdminLTE + global theme can tint body otherwise) */
  useEffect(() => {
    const body = document.body;
    const root = document.documentElement;
    const prevBg = body.style.backgroundColor;
    const prevRootBg = root.style.backgroundColor;
    const hadDark = body.classList.contains("dark-mode");

    root.style.backgroundColor = "#ffffff";
    body.style.setProperty("background-color", "#ffffff", "important");
    body.classList.remove("dark-mode");
    body.classList.add("light-mode");

    return () => {
      body.style.removeProperty("background-color");
      root.style.backgroundColor = prevRootBg;
      body.classList.remove("light-mode", "dark-mode");
      if (hadDark) body.classList.add("dark-mode");
      else body.classList.add("light-mode");
    };
  }, []);

  /** Subtle parallax on banner background (avoids iOS fixed-attachment quirks) */
  useEffect(() => {
    const section = parallaxSectionRef.current;
    const layer = parallaxLayerRef.current;
    if (!section || !layer) return;
    if (typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }

    let rafId = 0;
    const update = () => {
      rafId = 0;
      const rect = section.getBoundingClientRect();
      const vh = window.innerHeight;
      if (rect.bottom < 0 || rect.top > vh) return;
      const centerOffset = rect.top + rect.height / 2 - vh / 2;
      const shift = centerOffset * 0.14;
      layer.style.transform = `translate3d(0, ${shift}px, 0)`;
    };

    const onScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "kwacha-landing-jsonld";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FinancialService",
      name: "Kwacha Plus",
      description:
        "Zambia's premier loan management platform for financial institutions. Loan processing, analytics, mobile access, and bank-grade security.",
      url: "https://kwachaplus.com/",
      areaServed: { "@type": "Country", name: "Zambia" },
      telephone: "+260-971-055-533",
      email: "support@kwachaplus.zm",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Lusaka",
        streetAddress: "Cairo Road, City Center",
        addressCountry: "ZM",
      },
    });
    document.head.appendChild(script);
    return () => {
      document.getElementById("kwacha-landing-jsonld")?.remove();
    };
  }, []);

  const year = new Date().getFullYear();

  return (
    <div
      id="kwacha-landing"
      className="min-h-screen bg-white font-body text-on-surface antialiased [-webkit-font-smoothing:antialiased]"
    >
      <header className="landing-top-nav sticky top-0 z-50 bg-white px-margin-mobile md:px-margin-desktop">
        <nav
          className="mx-auto flex w-full max-w-container-max flex-wrap items-center gap-x-3 gap-y-2 py-3 md:flex-nowrap md:items-center md:gap-x-4 md:py-3"
          aria-label="Main navigation"
        >
          <Link
            to="/"
            className="order-1 inline-flex min-w-0 max-w-[min(65vw,240px)] shrink-0 items-center no-underline transition-opacity hover:opacity-90 md:max-w-none"
            aria-label="Kwacha Plus home"
          >
            <img
              src="/images/logo.png"
              alt="Kwacha Plus"
              className="h-11 w-auto max-h-[48px] max-w-full object-contain object-left sm:h-12 sm:max-h-[52px] md:h-14 md:max-h-[60px] lg:h-[68px] lg:max-h-none"
              width={460}
              height={102}
            />
          </Link>

          <div className="order-3 flex min-h-[40px] w-full min-w-0 basis-full items-center justify-start gap-x-4 overflow-x-auto whitespace-nowrap px-0 py-1 [-ms-overflow-style:none] [scrollbar-width:none] sm:justify-center sm:gap-x-5 md:order-2 md:w-auto md:flex-1 md:basis-auto md:justify-center md:gap-x-6 md:px-2 md:py-0 lg:gap-x-8 [&::-webkit-scrollbar]:hidden">
            {primaryNavLinks.map(({ label, id }) => {
              const isActive = activeSectionId === id;
              return (
                <button
                  key={id}
                  type="button"
                  aria-current={isActive ? "true" : undefined}
                  onClick={() => {
                    scrollToId(id);
                    setNavOpen(false);
                  }}
                  className={`landing-nav-link relative shrink-0 border-t-2 bg-transparent px-2 pb-1 pt-2 text-left text-[11px] font-medium tracking-tight transition-colors focus-visible:outline focus-visible:ring-2 focus-visible:ring-accent/50 focus-visible:ring-offset-2 sm:px-3 sm:text-[12px] md:px-4 md:text-[13px] lg:text-[14px] ${
                    isActive ? "border-accent font-semibold" : "border-transparent"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>

          <div className="order-2 ml-auto flex shrink-0 items-center justify-end gap-2 sm:gap-3 md:order-3 md:ml-0">
            {loggedIn ? (
              <Link
                to={accountPath}
                className="landing-nav-account-btn inline-flex items-center rounded-md px-4 py-2 text-sm font-semibold"
              >
                My account
              </Link>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => navigate("/signup")}
                  className="landing-nav-signup hidden rounded-md px-6 py-2.5 text-base font-bold md:inline-flex"
                >
                  Get Started
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className="landing-nav-login inline-flex rounded-md px-6 py-2.5 text-base font-semibold"
                >
                  Log In
                </button>
              </>
            )}

            <button
              type="button"
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-neutral-100 text-primary transition-colors hover:bg-neutral-200 md:hidden"
              aria-label={navOpen ? "Close menu" : "Open menu"}
              aria-expanded={navOpen}
              onClick={() => setNavOpen((o) => !o)}
            >
              <MenuOutlined className="text-lg" />
            </button>
          </div>
        </nav>

        {navOpen && (
          <div className="border-t border-neutral-100 bg-neutral-50 py-5 md:hidden">
            <div className="flex flex-col gap-0.5">
              {primaryNavLinks.map(({ label, id }) => {
                const isActive = activeSectionId === id;
                return (
                  <button
                    key={id}
                    type="button"
                    className={`rounded-lg px-4 py-3.5 text-left text-[15px] font-medium transition-colors ${
                      isActive ? "bg-white font-semibold text-[#4b5563]" : "text-[#6b7280] hover:bg-white"
                    }`}
                    onClick={() => {
                      scrollToId(id);
                      setNavOpen(false);
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <div className="mt-5 flex flex-col gap-3">
              {loggedIn ? (
                <Link to={accountPath} className="landing-nav-account-btn flex items-center justify-center rounded-md py-3 text-center text-sm font-semibold">
                  My account
                </Link>
              ) : (
                <>
                  <button type="button" className="landing-nav-login w-full rounded-md py-3 text-sm font-semibold" onClick={() => navigate("/login")}>
                    Log In
                  </button>
                  <button type="button" className="landing-nav-signup w-full rounded-md py-3 text-sm font-bold" onClick={() => navigate("/signup")}>
                    Get Started
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="bg-white">
        {/* Hero */}
        <section className="landing-hero relative isolate overflow-hidden bg-white px-margin-mobile pb-16 pt-10 md:px-margin-desktop md:pb-section-padding md:pt-14">
          <div className="mx-auto grid max-w-container-max items-center gap-12 lg:gap-16 lg:grid-cols-2">
            <div className="relative z-10">
              <p className="mb-3 text-label-md font-bold uppercase tracking-widest text-accent">Kwacha Plus</p>
              <h1 className="font-headline text-[clamp(2.25rem,4vw+1rem,4rem)] font-extrabold leading-[1.05] tracking-[-0.03em] !text-primary lg:text-headline-xl">
                Simplify Your{" "}
                <span className="text-accent underline decoration-accent/30 underline-offset-8">Lending</span> Journey
              </h1>
              <p className="mb-6 mt-6 max-w-xl text-body-lg text-on-surface-variant">
                Zambia&apos;s premier loan management solution trusted by financial institutions nationwide. Automate operations, enhance decision-making,
                and accelerate growth.
              </p>
              <div className="mb-8 flex flex-wrap gap-3">
                <span className="inline-flex items-center rounded-full border border-primary/10 bg-primary/[0.04] px-4 py-2 text-sm font-semibold !text-primary">
                  Bank of Zambia Certified
                </span>
                <span className="inline-flex items-center rounded-full border border-neutral-200 bg-neutral-50 px-4 py-2 text-sm font-medium text-on-surface-variant">
                  ISO 27001 • PCI DSS
                </span>
              </div>
              <div className="flex flex-wrap gap-4">
                {loggedIn ? (
                  <Link
                    to={accountPath}
                    className="btn-primary inline-flex items-center justify-center rounded-full px-10 py-4 text-button"
                  >
                    My account
                  </Link>
                ) : (
                  <button type="button" onClick={() => navigate("/signup")} className="btn-primary rounded-full px-10 py-4 text-button">
                    Get Started
                  </button>
                )}
                <button type="button" onClick={() => scrollToId("features")} className="btn-secondary rounded-full px-10 py-4 text-button">
                  Explore Features
                </button>
              </div>
            </div>
            <div className="relative">
              <div className="hero-shadow relative flex min-h-[320px] items-center justify-center overflow-hidden rounded-[2rem] bg-neutral-50 md:min-h-[440px] lg:min-h-[560px]">
                <img
                  src="/images/zz.webp"
                  alt="Kwacha Plus platform dashboard — loan management analytics and portfolio overview"
                  className="h-auto w-full max-h-[min(72vh,680px)] object-contain"
                  width={800}
                  height={600}
                  loading="eager"
                  decoding="async"
                />
                <div className="pointer-events-none absolute bottom-4 left-4 right-4 flex flex-wrap justify-center gap-2 md:justify-end">
                  <span className="rounded-xl bg-white/95 px-4 py-2 text-sm font-bold shadow-soft backdrop-blur-sm !text-primary">
                    98.5% Recovery Rate
                  </span>
                  <span className="rounded-xl bg-white/95 px-4 py-2 text-sm font-bold shadow-soft backdrop-blur-sm !text-primary">
                    60% Faster Processing
                  </span>
                </div>
              </div>
              <p className="mt-3 text-center text-sm font-medium text-on-surface-variant md:text-left">Platform Dashboard</p>
            </div>
          </div>
        </section>

        {/* Parallax CTA banner */}
        <section
          ref={parallaxSectionRef}
          className="landing-parallax-banner relative isolate flex min-h-[min(68vh,520px)] items-center overflow-hidden md:min-h-[min(72vh,600px)]"
          aria-labelledby="parallax-cta-heading"
        >
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div
              ref={parallaxLayerRef}
              className="absolute -left-[8%] -right-[8%] -top-[12%] bottom-[-12%] will-change-transform"
              style={{ transform: "translate3d(0,0,0)" }}
            >
              <img
                src="https://img.magnific.com/premium-photo/woman-with-digital-tablet_1320745-29321.jpg?w=1480"
                alt=""
                className="h-full w-full object-cover object-[center_28%]"
                width={1480}
                height={987}
                loading="lazy"
                decoding="async"
                fetchPriority="low"
              />
            </div>
          </div>
          <div className="absolute inset-0 bg-black/50 md:bg-black/45" aria-hidden />
          <div
            className="absolute inset-0 bg-gradient-to-br from-[#0a1d37]/92 via-[#0a1d37]/78 to-[#0a1d37]/68 md:from-[#0a1d37]/88 md:via-[#0a1d37]/72 md:to-black/55"
            aria-hidden
          />
          <div className="relative z-10 mx-auto w-full max-w-container-max px-margin-mobile py-16 md:px-margin-desktop md:py-24">
            <p className="mb-5 text-label-md font-bold uppercase tracking-widest text-accent md:mb-6">Kwacha Plus</p>
            <h2
              id="parallax-cta-heading"
              className="font-headline w-full max-w-none text-balance text-[clamp(2.85rem,7.5vw+1.25rem,5.25rem)] font-extrabold leading-[1.02] tracking-[-0.035em] text-white drop-shadow-[0_4px_36px_rgba(0,0,0,0.6)] sm:text-[clamp(3.25rem,8vw+1.5rem,6rem)] lg:text-[clamp(3.75rem,8.5vw+1.75rem,7rem)]"
            >
              Simplify your loan business today
            </h2>
            <div className="mt-8 max-w-xl md:mt-10 md:max-w-2xl">
              <p className="max-w-lg text-lg leading-relaxed text-white md:text-xl">
                Run lending operations with clarity — from application to recovery — on one platform built for Zambia&apos;s institutions.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <button
                  type="button"
                  onClick={() => navigate("/signup")}
                  className="btn-primary rounded-full px-10 py-4 text-button shadow-lg shadow-black/20"
                >
                  Join KwachaPlus today
                </button>
                <button
                  type="button"
                  onClick={() => scrollToId("features")}
                  className="inline-flex items-center justify-center rounded-full border-2 border-white/90 bg-white/10 px-10 py-4 text-button font-bold text-white backdrop-blur-sm transition-colors hover:bg-white/20"
                >
                  See features
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="relative isolate overflow-hidden px-margin-mobile py-16 md:px-margin-desktop md:py-20" aria-labelledby="stats-heading">
          <img
            src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=1600&q=80"
            alt=""
            aria-hidden
            className="absolute inset-0 -z-10 h-full w-full object-cover"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 -z-10 bg-primary/85" aria-hidden />
          <div className="absolute inset-0 -z-10 bg-gradient-to-br from-primary/95 via-primary/85 to-primary/70" aria-hidden />
          <div className="mx-auto max-w-container-max">
            <h2 id="stats-heading" className="sr-only">
              Kwacha Plus at a glance
            </h2>
            <div className="grid gap-y-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-y-0 lg:divide-x lg:divide-white/15">
              <div className="text-center lg:px-6">
                <div className="font-headline text-[clamp(2rem,4vw,2.75rem)] font-extrabold text-white">250+</div>
                <div className="mt-1 text-body-md font-medium text-white/70">Clients</div>
              </div>
              <div className="text-center lg:px-6">
                <div className="font-headline text-[clamp(2rem,4vw,2.75rem)] font-extrabold text-white">10,000+</div>
                <div className="mt-1 text-body-md font-medium text-white/70">Loans Processed</div>
              </div>
              <div className="text-center lg:px-6">
                <div className="font-headline text-[clamp(2rem,4vw,2.75rem)] font-extrabold text-accent">60% faster</div>
                <div className="mt-1 text-body-md font-medium text-white/70">Processing Time</div>
              </div>
              <div className="text-center lg:px-6">
                <div className="font-headline text-[clamp(2rem,4vw,2.75rem)] font-extrabold text-accent">98.5%</div>
                <div className="mt-1 text-body-md font-medium text-white/70">Recovery Rate</div>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section id="features" className="relative overflow-hidden bg-white px-margin-mobile py-section-padding md:px-margin-desktop">
          <div className="pointer-events-none absolute -right-40 top-16 h-96 w-96 rounded-full bg-accent/15 blur-3xl" aria-hidden />
          <div className="relative mx-auto max-w-container-max">
            <div className="mb-14 text-center md:mb-20 md:text-left">
              <h2 className="mb-4 font-headline text-headline-lg !text-primary">Powerful Features for Modern Lenders</h2>
              <div className="mx-auto h-2 w-24 rounded-full bg-accent md:mx-0" />
            </div>
            <div className="grid gap-10 md:grid-cols-2 lg:gap-12 xl:grid-cols-4">
              {features.map((f) => (
                <article key={f.title} className="group rounded-2xl border border-neutral-100 bg-white p-8 shadow-soft transition-shadow hover:shadow-glow">
                  <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-accent/10 text-accent transition-colors group-hover:bg-accent/20">
                    <span className="material-symbols-outlined text-3xl">{f.icon}</span>
                  </div>
                  <h3 className="mb-3 font-headline text-xl font-bold !text-primary">{f.title}</h3>
                  <p className="text-body-md leading-relaxed text-on-surface-variant">{f.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Steps — Solutions */}
        <section
          id="steps"
          className="relative overflow-hidden border-t border-neutral-100 bg-gradient-to-b from-accent/[0.08] via-neutral-50/60 to-white px-margin-mobile py-section-padding md:px-margin-desktop"
          aria-labelledby="steps-heading"
        >
          <div className="pointer-events-none absolute -left-40 top-24 h-80 w-80 rounded-full bg-accent/15 blur-3xl" aria-hidden />
          <div className="pointer-events-none absolute -bottom-20 -right-32 h-72 w-72 rounded-full bg-primary/[0.06] blur-3xl" aria-hidden />

          <div className="relative mx-auto max-w-container-max">
            <div className="mb-14 text-center md:mb-16">
              <p className="mb-3 text-label-md font-bold uppercase tracking-widest text-accent">How it works</p>
              <h2 id="steps-heading" className="font-headline text-[clamp(1.75rem,3vw+1rem,2.5rem)] font-extrabold !text-primary">
                Three Simple Steps
              </h2>
              <div className="mx-auto mt-5 h-1.5 w-20 rounded-full bg-accent shadow-[0_0_24px_rgba(152,217,76,0.45)]" />
              <p className="mx-auto mt-8 max-w-3xl text-pretty text-lg font-medium leading-relaxed text-[#4b5563] md:text-xl">
                From application to disbursement — a streamlined journey for your borrowers and your operations team.
              </p>
            </div>

            <div className="relative grid gap-8 sm:gap-10 md:grid-cols-3 md:gap-6 lg:gap-8">
              {/* Timeline connector (desktop) */}
              <div
                className="pointer-events-none absolute left-[16.666%] right-[16.666%] top-[3.25rem] hidden h-[3px] rounded-full bg-gradient-to-r from-accent/20 via-accent/50 to-accent/20 md:block"
                aria-hidden
              />

              {steps.map((s, i) => (
                <article
                  key={s.title}
                  className="group relative flex flex-col items-center rounded-[1.75rem] border border-neutral-100/90 bg-white/95 p-8 text-center shadow-soft backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-accent/30 hover:shadow-glow md:pt-10"
                >
                  <div className="relative mb-7">
                    <div className="relative flex h-[4.5rem] w-[4.5rem] items-center justify-center rounded-2xl bg-gradient-to-br from-accent/25 via-accent/10 to-white text-accent shadow-inner ring-1 ring-accent/25 transition-transform duration-300 group-hover:scale-105 group-hover:ring-accent/40">
                      <span className="material-symbols-outlined text-[2.35rem]">{s.icon}</span>
                    </div>
                    <span className="absolute -right-1 -top-1 flex h-9 min-w-[2.25rem] items-center justify-center rounded-full bg-primary px-2 text-sm font-bold tabular-nums text-accent shadow-md ring-[3px] ring-white">
                      {i + 1}
                    </span>
                  </div>
                  <h3 className="mb-4 font-headline text-xl font-bold leading-snug !text-primary md:text-[1.35rem]">{s.title}</h3>
                  <p className="max-w-[22rem] text-base leading-relaxed text-[#4b5563] md:text-[1.0625rem]">{s.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Mobile experience — Company */}
        <section id="mobile" className="relative overflow-hidden bg-neutral-50/40 px-margin-mobile py-section-padding md:px-margin-desktop">
          <div className="pointer-events-none absolute -right-32 bottom-8 h-80 w-80 rounded-full bg-accent/15 blur-3xl" aria-hidden />
          <div className="relative mx-auto grid max-w-container-max items-center gap-16 md:grid-cols-2 md:gap-24">
            <div className="order-2 flex justify-center md:order-1">
              <div className="relative w-full max-w-[320px]">
                <div className="absolute -inset-6 -z-10 rounded-[3rem] bg-gradient-to-br from-accent/20 via-transparent to-primary/10 blur-2xl" aria-hidden />
                <div className="relative overflow-hidden rounded-[2.5rem] border-[10px] border-primary shadow-[0_24px_60px_-20px_rgba(10,29,55,0.45)]">
                  <img
                    src="/images/zapp.webp"
                    alt="Kwacha Plus responsive dashboard on mobile"
                    className="aspect-[9/16] w-full object-cover object-top"
                    width={320}
                    height={568}
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <span className="mb-6 inline-block rounded-full bg-accent/15 px-5 py-2 text-label-md font-bold uppercase tracking-widest text-accent">
                Mobile Experience
              </span>
              <h2 className="mb-6 font-headline text-[clamp(2rem,4vw,3rem)] font-extrabold leading-tight tracking-[-0.03em] !text-primary">
                Kwacha Plus on the Go
              </h2>
              <p className="mb-10 text-body-lg text-on-surface-variant">
                Manage your loans, track payments, and serve teams in the field from any device. Our responsive interface keeps administrators and loan officers productive wherever work happens.
              </p>
              <div className="flex flex-wrap gap-4">
                <button
                  type="button"
                  onClick={() => setDemoOpen(true)}
                  className="btn-secondary inline-flex items-center gap-3 rounded-full px-8 py-3 text-button"
                >
                  <span className="material-symbols-outlined text-2xl !text-primary">smartphone</span>
                  Book a product tour
                </button>
                <button type="button" onClick={() => scrollToId("features")} className="btn-primary rounded-full px-8 py-3 text-button">
                  View capabilities
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section
          id="testimonials"
          className="relative isolate overflow-hidden bg-gradient-to-b from-neutral-50 via-accent/[0.04] to-white px-margin-mobile py-section-padding md:px-margin-desktop"
        >
          {/* Dotted grid texture */}
          <div
            className="pointer-events-none absolute inset-0 -z-10 opacity-[0.5] [background-image:radial-gradient(circle,rgba(10,29,55,0.07)_1px,transparent_1px)] [background-size:22px_22px] [mask-image:radial-gradient(ellipse_at_center,black,transparent_75%)]"
            aria-hidden
          />
          {/* Accent glows — balanced on both sides */}
          <div className="pointer-events-none absolute -left-40 top-20 -z-10 h-80 w-80 rounded-full bg-accent/20 blur-3xl" aria-hidden />
          <div className="pointer-events-none absolute -right-32 bottom-10 -z-10 h-72 w-72 rounded-full bg-primary/[0.06] blur-3xl" aria-hidden />
          <div className="relative mx-auto max-w-container-max">
            <div className="mb-12 text-center md:mb-16">
              <span className="mb-5 inline-flex items-center gap-2 rounded-full border border-accent/20 bg-accent/10 px-4 py-1.5 text-label-md font-bold uppercase tracking-widest text-accent">
                💎 Client Success Stories
              </span>
              <h2 className="mx-auto max-w-3xl font-headline text-headline-lg !text-primary">
                See Why <span className="text-accent">250+ Institutions</span> Choose Kwacha Plus
              </h2>
              <div className="mx-auto mt-5 h-1.5 w-20 rounded-full bg-accent shadow-[0_0_24px_rgba(152,217,76,0.45)]" />
              <p className="mx-auto mt-7 max-w-2xl text-body-lg text-on-surface-variant">
                Real results from real businesses across Zambia 🇿🇲
              </p>
            </div>

            <div className="hidden gap-8 lg:grid lg:grid-cols-3">
              {testimonials.map((t) => (
                <figure key={t.author} className="flex h-full flex-col rounded-2xl border border-neutral-100 bg-white p-8 shadow-soft">
                  <blockquote className="flex flex-1 flex-col">
                    <StarRow />
                    <p className="mb-6 flex-1 text-body-md leading-relaxed text-on-surface">&ldquo;{t.quote}&rdquo;</p>
                  </blockquote>
                  <figcaption className="mt-auto border-t border-neutral-100 pt-6">
                    <cite className="not-italic">
                      <span className="block font-headline font-bold !text-primary">{t.author}</span>
                      <span className="text-sm text-on-surface-variant">{t.org}</span>
                    </cite>
                    <span className="mt-2 inline-block rounded-full bg-accent/15 px-3 py-1 text-xs font-bold uppercase tracking-wide text-accent">Verified Client</span>
                  </figcaption>
                </figure>
              ))}
            </div>

            <div className="lg:hidden">
              <figure className="rounded-2xl border border-neutral-100 bg-white p-8 shadow-soft">
                <blockquote>
                  <StarRow />
                  <p className="mb-6 text-body-md leading-relaxed text-on-surface">&ldquo;{testimonials[testimonialIdx].quote}&rdquo;</p>
                </blockquote>
                <figcaption className="border-t border-neutral-100 pt-6">
                  <cite className="not-italic">
                    <span className="block font-headline font-bold !text-primary">{testimonials[testimonialIdx].author}</span>
                    <span className="text-sm text-on-surface-variant">{testimonials[testimonialIdx].org}</span>
                  </cite>
                  <span className="mt-2 inline-block rounded-full bg-accent/15 px-3 py-1 text-xs font-bold uppercase tracking-wide text-accent">Verified Client</span>
                </figcaption>
              </figure>
              <div className="mt-8 flex justify-center gap-2" role="tablist" aria-label="Testimonial slides">
                {testimonials.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    role="tab"
                    aria-selected={testimonialIdx === i}
                    aria-label={`Show testimonial ${i + 1}`}
                    className={`h-2.5 w-2.5 rounded-full transition-colors ${testimonialIdx === i ? "bg-accent" : "bg-neutral-300"}`}
                    onClick={() => setTestimonialIdx(i)}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Access */}
        <section id="access" className="relative overflow-hidden bg-white px-margin-mobile py-section-padding md:px-margin-desktop">
          <div className="pointer-events-none absolute -right-32 top-10 h-80 w-80 rounded-full bg-accent/10 blur-3xl" aria-hidden />
          <div className="pointer-events-none absolute -left-24 bottom-0 h-72 w-72 rounded-full bg-primary/[0.05] blur-3xl" aria-hidden />

          <div className="relative mx-auto grid max-w-container-max items-center gap-14 lg:grid-cols-2 lg:gap-20">
            {/* Intro */}
            <div>
              <span className="mb-5 inline-block rounded-full bg-accent/15 px-4 py-1.5 text-label-md font-bold uppercase tracking-widest text-accent">
                Secure Access
              </span>
              <h2 className="mb-6 font-headline text-headline-lg !text-primary">Access Your Account</h2>
              <p className="mb-8 max-w-md text-body-lg text-on-surface-variant">
                Sign in to manage your portfolio, configure settings, and oversee operations — all from one secure, centralized dashboard.
              </p>
              <ul className="flex flex-col gap-4">
                {[
                  { icon: "verified_user", text: "Bank-grade encryption & two-factor authentication" },
                  { icon: "admin_panel_settings", text: "Role-based access for every team member" },
                  { icon: "schedule", text: "99.9% uptime — available whenever you need it" },
                ].map((item) => (
                  <li key={item.icon} className="flex items-center gap-3 text-body-md text-on-surface">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent">
                      <span className="material-symbols-outlined text-xl">{item.icon}</span>
                    </span>
                    {item.text}
                  </li>
                ))}
              </ul>
            </div>

            {/* Login card */}
            <div className="mx-auto w-full max-w-md lg:mx-0">
              <div className="overflow-hidden rounded-3xl border border-neutral-200 bg-white shadow-soft">
                <div className="flex items-center gap-4 bg-gradient-to-br from-primary to-[#13294d] p-7">
                  <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-accent text-2xl font-extrabold text-primary shadow-md">A</span>
                  <div>
                    <h3 className="font-headline text-xl font-bold text-white">Administrator</h3>
                    <p className="text-sm text-white/70">Secure portal access</p>
                  </div>
                </div>
                <div className="p-7">
                  <p className="mb-7 text-body-md leading-relaxed text-on-surface-variant">
                    Manage users, configure system settings, and oversee all operations from one centralized dashboard.
                  </p>
                  {loggedIn ? (
                    <Link to={accountPath} className="btn-primary inline-flex w-full items-center justify-center rounded-full py-3.5 text-center text-button">
                      Go to dashboard
                    </Link>
                  ) : (
                    <button type="button" onClick={() => navigate("/login")} className="btn-primary w-full rounded-full py-3.5 text-button">
                      Log In to Dashboard
                    </button>
                  )}
                  <p className="mt-5 flex items-center justify-center gap-2 text-sm text-on-surface-variant">
                    <span className="material-symbols-outlined text-base text-accent">lock</span>
                    256-bit SSL secured connection
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* About */}
        <section id="about" className="border-t border-neutral-100 bg-white px-margin-mobile py-section-padding md:px-margin-desktop">
          <div className="mx-auto max-w-container-max">
            <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
              <div>
                <span className="mb-5 inline-block rounded-full bg-accent/15 px-4 py-1.5 text-label-md font-bold uppercase tracking-widest text-accent">
                  Our Story
                </span>
                <h2 className="mb-6 font-headline text-headline-lg !text-primary">About Kwacha Plus</h2>
                <div className="space-y-5 text-body-md leading-relaxed text-on-surface-variant">
                  <p>
                    Kwacha Plus is Zambia&apos;s leading loan management platform, purpose-built for African financial institutions. Founded in 2020,
                    we&apos;ve revolutionized how lenders manage their operations, serving over 250 institutions across the country.
                  </p>
                  <p>
                    Our mission is to democratize access to financial technology, empowering lenders of all sizes to operate with the efficiency and
                    sophistication of major banks. We combine local market expertise with world-class technology to deliver solutions that truly work for
                    Zambian businesses.
                  </p>
                </div>
                <div className="mt-10 grid gap-4 sm:grid-cols-3">
                  <div className="rounded-2xl border border-neutral-100 bg-neutral-50/80 p-6 text-center">
                    <div className="font-headline text-2xl font-extrabold !text-primary">250+</div>
                    <div className="text-sm font-medium text-on-surface-variant">Institutions</div>
                  </div>
                  <div className="rounded-2xl border border-neutral-100 bg-neutral-50/80 p-6 text-center">
                    <div className="font-headline text-2xl font-extrabold !text-primary">50K+</div>
                    <div className="text-sm font-medium text-on-surface-variant">Active Users</div>
                  </div>
                  <div className="rounded-2xl border border-neutral-100 bg-neutral-50/80 p-6 text-center">
                    <div className="font-headline text-2xl font-extrabold text-accent">99.9%</div>
                    <div className="text-sm font-medium text-on-surface-variant">Uptime</div>
                  </div>
                </div>
              </div>
              <div className="relative">
                <div className="pointer-events-none absolute -inset-4 -z-10 rounded-[2.5rem] bg-gradient-to-br from-accent/20 via-transparent to-primary/10 blur-2xl" aria-hidden />
                <div className="hero-shadow overflow-hidden rounded-[2rem]">
                  <img
                    src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1200&q=80"
                    alt="Kwacha Plus team collaborating on lending operations"
                    className="aspect-[4/3] w-full object-cover"
                    width={1200}
                    height={900}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="absolute -bottom-5 -left-5 hidden rounded-2xl bg-white px-6 py-4 shadow-soft sm:block">
                  <div className="font-headline text-2xl font-extrabold !text-primary">Since 2020</div>
                  <div className="text-sm font-medium text-on-surface-variant">Trusted across Zambia</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Also From Us */}
        <section id="solutions" className="border-t border-neutral-100 bg-neutral-50/40 px-margin-mobile py-section-padding md:px-margin-desktop">
          <div className="mx-auto max-w-container-max">
            <div className="mx-auto max-w-2xl text-center">
              <span className="mb-5 inline-block rounded-full bg-accent/15 px-4 py-1.5 text-label-md font-bold uppercase tracking-widest text-accent">
                Also From Us
              </span>
              <h2 className="mb-4 font-headline text-headline-lg !text-primary">More Tools to Grow Your Business</h2>
              <p className="text-body-md text-on-surface-variant">Beyond lending, we build software Zambian businesses actually need.</p>
            </div>
            <div className="mx-auto mt-12 max-w-3xl rounded-2xl border border-neutral-100 bg-white p-8 shadow-soft md:flex md:items-center md:gap-8 md:p-10">
              <div className="flex-1">
                <span className="material-symbols-outlined mb-4 text-3xl text-accent">receipt_long</span>
                <h3 className="mb-2 font-headline text-xl font-bold !text-primary">ZedInvoice</h3>
                <p className="text-body-md text-on-surface-variant">
                  Professional invoicing for Zambian businesses — branded ZMW invoices, automated payment reminders, and mobile money collections. Free 7-day trial, then K49/month.
                </p>
              </div>
              <div className="mt-6 shrink-0 md:mt-0">
                <a
                  href="https://zedinvoice.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center rounded-full bg-primary px-8 py-3.5 text-base font-bold text-white transition-opacity hover:opacity-95"
                >
                  Visit ZedInvoice →
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="relative overflow-hidden bg-neutral-50/60 px-margin-mobile py-section-padding md:px-margin-desktop">
          <div className="pointer-events-none absolute -right-36 top-24 h-80 w-80 rounded-full bg-accent/15 blur-3xl" aria-hidden />
          <div className="relative mx-auto max-w-container-max">
            <div className="mb-12 text-center md:text-left">
              <h2 className="mb-4 font-headline text-headline-lg !text-primary">Get in Touch</h2>
              <p className="max-w-xl text-body-md text-on-surface-variant">Have questions? We&apos;re here to help you transform your lending operations.</p>
            </div>
            <div className="grid gap-8 md:grid-cols-3">
              <div className="rounded-2xl border border-neutral-100 bg-white p-8 shadow-soft">
                <span className="material-symbols-outlined mb-4 text-3xl text-accent">call</span>
                <h3 className="mb-2 font-headline font-bold !text-primary">Call Us</h3>
                <a href="tel:+260971055533" className="text-body-md font-semibold !text-primary hover:text-accent">
                  +260 971 055 533
                </a>
                <p className="mt-2 text-sm text-on-surface-variant">Mon–Fri 8am–5pm</p>
              </div>
              <div className="rounded-2xl border border-neutral-100 bg-white p-8 shadow-soft">
                <span className="material-symbols-outlined mb-4 text-3xl text-accent">mail</span>
                <h3 className="mb-2 font-headline font-bold !text-primary">Email Us</h3>
                <a href="mailto:support@kwachaplus.zm" className="break-all text-body-md font-semibold !text-primary hover:text-accent">
                  support@kwachaplus.zm
                </a>
                <p className="mt-2 text-sm text-on-surface-variant">24–48 hour response</p>
              </div>
              <address className="not-italic rounded-2xl border border-neutral-100 bg-white p-8 shadow-soft">
                <span className="material-symbols-outlined mb-4 text-3xl text-accent">location_on</span>
                <h3 className="mb-2 font-headline font-bold !text-primary">Visit Us</h3>
                <p className="text-body-md font-semibold !text-primary">Lusaka, Zambia</p>
                <p className="mt-1 text-body-md text-on-surface-variant">Cairo Road, City Center</p>
              </address>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section id="cta" className="bg-primary px-margin-mobile py-20 text-white md:px-margin-desktop md:py-24">
          <div className="mx-auto max-w-container-max text-center">
            <h2 className="mb-4 font-headline text-[clamp(1.75rem,3vw,2.5rem)] font-extrabold leading-tight text-white">
              Ready to Transform Your Lending Operations?
            </h2>
            <p className="mx-auto mb-10 max-w-2xl text-lg text-white/85">
              Join 250+ financial institutions already using Kwacha Plus to revolutionize their lending processes
            </p>
            <div className="mb-12 flex flex-wrap justify-center gap-10 border-y border-white/15 py-8 md:gap-16">
              <div>
                <div className="font-headline text-3xl font-extrabold text-accent">250+</div>
                <div className="text-sm text-white/75">Happy Clients</div>
              </div>
              <div>
                <div className="font-headline text-3xl font-extrabold text-accent">60%</div>
                <div className="text-sm text-white/75">Faster Processing</div>
              </div>
              <div>
                <div className="font-headline text-3xl font-extrabold text-accent">98.5%</div>
                <div className="text-sm text-white/75">Recovery Rate</div>
              </div>
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              {loggedIn ? (
                <Link
                  to={accountPath}
                  className="inline-flex items-center justify-center rounded-full bg-accent px-10 py-4 text-base font-bold text-white transition-opacity hover:opacity-95"
                >
                  My account
                </Link>
              ) : (
                <button type="button" onClick={() => navigate("/signup")} className="rounded-full bg-accent px-10 py-4 text-base font-bold text-white transition-opacity hover:opacity-95">
                  Get Started Today
                </button>
              )}
              <button
                type="button"
                onClick={() => setDemoOpen(true)}
                className="rounded-full bg-white/15 px-10 py-4 text-base font-bold text-white hover:bg-white/25"
              >
                Book a Demo
              </button>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer id="footer" className="bg-primary text-white">
        <div className="mx-auto max-w-container-max px-margin-mobile pt-section-padding md:px-margin-desktop">
          <div className="grid gap-12 pb-16 md:grid-cols-2 lg:grid-cols-12 lg:gap-8">
            {/* Brand + contact */}
            <div className="lg:col-span-4">
              <div className="mb-6 inline-flex items-center rounded-xl bg-white px-3 py-2">
                <img src="/images/logo.png" alt="Kwacha Plus" className="h-9 w-auto md:h-10" width={260} height={52} />
              </div>
              <p className="max-w-sm text-body-md leading-relaxed text-white/65">
                Empowering financial institutions across Zambia with innovative technology for efficient lending operations.
              </p>
              <ul className="mt-7 flex flex-col gap-3 text-body-md">
                <li>
                  <a href="tel:+260971055533" className="group inline-flex items-center gap-3 text-white/75 transition-colors hover:text-accent">
                    <span className="material-symbols-outlined text-xl text-accent">call</span>
                    +260 971 055 533
                  </a>
                </li>
                <li>
                  <a href="mailto:support@kwachaplus.zm" className="group inline-flex items-center gap-3 break-all text-white/75 transition-colors hover:text-accent">
                    <span className="material-symbols-outlined text-xl text-accent">mail</span>
                    support@kwachaplus.zm
                  </a>
                </li>
                <li className="inline-flex items-center gap-3 text-white/75">
                  <span className="material-symbols-outlined text-xl text-accent">location_on</span>
                  Cairo Road, Lusaka, Zambia
                </li>
              </ul>
            </div>

            {/* Link columns */}
            <div className="lg:col-span-2 lg:col-start-7">
              <h5 className="mb-5 font-headline text-sm font-bold uppercase tracking-wider text-white">Product</h5>
              <ul className="flex flex-col gap-3.5 text-body-md">
                <li>
                  <button type="button" className="bg-transparent p-0 text-left text-white/65 transition-colors hover:text-accent" onClick={() => scrollToId("features")}>
                    Features
                  </button>
                </li>
                <li>
                  <button type="button" className="bg-transparent p-0 text-left text-white/65 transition-colors hover:text-accent" onClick={() => setDemoOpen(true)}>
                    Pricing
                  </button>
                </li>
                <li>
                  <button type="button" className="bg-transparent p-0 text-left text-white/65 transition-colors hover:text-accent" onClick={() => scrollToId("features")}>
                    Security
                  </button>
                </li>
                <li>
                  <button type="button" className="bg-transparent p-0 text-left text-white/65 transition-colors hover:text-accent" onClick={() => setDemoOpen(true)}>
                    Updates
                  </button>
                </li>
              </ul>
            </div>
            <div className="lg:col-span-2">
              <h5 className="mb-5 font-headline text-sm font-bold uppercase tracking-wider text-white">Company</h5>
              <ul className="flex flex-col gap-3.5 text-body-md">
                <li>
                  <button type="button" className="bg-transparent p-0 text-left text-white/65 transition-colors hover:text-accent" onClick={() => scrollToId("about")}>
                    About Us
                  </button>
                </li>
                <li>
                  <button type="button" className="bg-transparent p-0 text-left text-white/65 transition-colors hover:text-accent" onClick={() => setDemoOpen(true)}>
                    Careers
                  </button>
                </li>
                <li>
                  <button type="button" className="bg-transparent p-0 text-left text-white/65 transition-colors hover:text-accent" onClick={() => scrollToId("contact")}>
                    Contact
                  </button>
                </li>
                <li>
                  <button type="button" className="bg-transparent p-0 text-left text-white/65 transition-colors hover:text-accent" onClick={() => setDemoOpen(true)}>
                    Partners
                  </button>
                </li>
              </ul>
            </div>
            <div className="lg:col-span-2">
              <h5 className="mb-5 font-headline text-sm font-bold uppercase tracking-wider text-white">Resources</h5>
              <ul className="flex flex-col gap-3.5 text-body-md">
                <li>
                  <button type="button" className="bg-transparent p-0 text-left text-white/65 transition-colors hover:text-accent" onClick={() => setDemoOpen(true)}>
                    Blog
                  </button>
                </li>
                <li>
                  <button type="button" className="bg-transparent p-0 text-left text-white/65 transition-colors hover:text-accent" onClick={() => setDemoOpen(true)}>
                    Community
                  </button>
                </li>
                <li>
                  <button type="button" className="bg-transparent p-0 text-left text-white/65 transition-colors hover:text-accent" onClick={() => setDemoOpen(true)}>
                    Support
                  </button>
                </li>
                <li>
                  <button type="button" className="bg-transparent p-0 text-left text-white/65 transition-colors hover:text-accent" onClick={() => setDemoOpen(true)}>
                    Documentation
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col gap-4 border-t border-white/10 py-8 text-center text-base text-white/70 md:flex-row md:items-center md:justify-between md:text-left">
            <p>© {year} Kwacha Plus. All rights reserved.</p>
            <p>Zambia&apos;s trusted loan management platform for banks, MFIs, and lenders nationwide.</p>
          </div>

          <div className="border-t border-white/10 py-6 text-center text-sm text-white/70">
            <p>
              Governed by the{" "}
              <a
                href="https://www.parliament.gov.zm/sites/default/files/documents/acts/Money-lenders%20Act.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/75 underline transition-colors hover:text-accent"
              >
                Money-Lenders Act (Cap 398) of the Laws of Zambia
              </a>
              .
            </p>
          </div>
        </div>
      </footer>

      <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-neutral-200 bg-white p-4 md:hidden">
        <div className="mx-auto flex max-w-lg gap-3">
          <button type="button" onClick={() => setDemoOpen(true)} className="h-12 flex-1 rounded-full border border-neutral-200 text-sm font-semibold text-primary">
            Book Demo
          </button>
          {loggedIn ? (
            <Link to={accountPath} className="btn-primary flex h-12 flex-[1.2] items-center justify-center rounded-full text-sm">
              My account
            </Link>
          ) : (
            <button type="button" onClick={() => navigate("/signup")} className="btn-primary h-12 flex-[1.2] rounded-full text-sm">
              Get Started
            </button>
          )}
        </div>
      </div>

      <DemoBookingModal visible={demoOpen} onClose={() => setDemoOpen(false)} />
    </div>
  );
}
